import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddBusinessIcon from "@mui/icons-material/AddBusinessOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import { IconButton, Tooltip } from "@mui/material";
import CompanyEnergyDialog from "../components/CompanyEnergyDialog";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  foyerApi,
  type CompanySummary,
  type PremiseSummary,
} from "../http/foyer";
import { useCan } from "../auth/AuthContext";
import DangerConfirmDialog from "../components/DangerConfirmDialog";
import { PageContainer, PageHeader } from "../components/PageHeader";
import { errorCode, friendlyError } from "../utils/apiError";

type CompanyType = "CUSTOMER" | "RESELLER" | "SYSTEM_INTEGRATOR" | "ADVERTISER";

const COMPANY_TYPES: Array<{ key: CompanyType; label: string }> = [
  { key: "CUSTOMER", label: "Customer (hotel)" },
  { key: "RESELLER", label: "Reseller" },
  { key: "SYSTEM_INTEGRATOR", label: "System integrator" },
  { key: "ADVERTISER", label: "Advertiser" },
];

/**
 * Top-level list of all Companies (tenants). Click a row to drill in.
 * Admins can create new tenants via the dialog.
 */
export default function CompaniesPage() {
  const navigate = useNavigate();
  const canManage = useCan("canManageCompanies");
  const [companies, setCompanies] = useState<CompanySummary[] | null>(null);
  const [premises, setPremises] = useState<PremiseSummary[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleting, setDeleting] = useState<CompanySummary | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteErr, setDeleteErr] = useState<string | null>(null);
  const [energyFor, setEnergyFor] = useState<CompanySummary | null>(null);
  const [toast, setToast] = useState<{
    msg: string;
    severity: "success" | "error";
  } | null>(null);

  async function refresh() {
    try {
      const [c, p] = await Promise.all([
        foyerApi.listCompanies(),
        foyerApi.listPremises(),
      ]);
      setCompanies(c);
      setPremises(p);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  const countByCompany = new Map<string, number>();
  for (const p of premises ?? []) {
    if (!p.companyId) continue;
    countByCompany.set(p.companyId, (countByCompany.get(p.companyId) ?? 0) + 1);
  }
  const unassigned = (premises ?? []).filter((p) => !p.companyId).length;

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Directory"
        title="Companies"
        subtitle="Tenants. A single company can own multiple premises."
        actions={
          canManage && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AddBusinessIcon />}
              onClick={() => setCreateOpen(true)}
              data-testid="companies-new"
            >
              New company
            </Button>
          )
        }
      />

      {err && (
        <Typography variant="body2" color="error">
          {err}
        </Typography>
      )}

      <Card sx={{ overflow: "hidden" }}>
        <TableContainer>
          <Table size="small" stickyHeader data-testid="companies-list">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Premises</TableCell>
                <TableCell sx={{ width: 104 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {companies?.map((c) => {
                const n = countByCompany.get(c.id) ?? 0;
                const goto = () =>
                  navigate(`/premises?company=${encodeURIComponent(c.id)}`);
                return (
                  <TableRow
                    key={c.id}
                    hover
                    tabIndex={0}
                    role="button"
                    aria-label={`${c.name} premises`}
                    onClick={goto}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        goto();
                      }
                    }}
                    sx={{ cursor: "pointer" }}
                    data-testid={`company-row-${c.id}`}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {c.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={c.type} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="right">
                      {n} {n === 1 ? "premise" : "premises"}
                    </TableCell>
                    <TableCell
                      align="right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Tooltip title="Chain energy">
                        <IconButton
                          size="small"
                          aria-label={`Energy for ${c.name}`}
                          onClick={() => setEnergyFor(c)}
                          data-testid={`company-energy-${c.id}`}
                        >
                          <BoltOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canManage && (
                        <Tooltip title="Delete company">
                          <IconButton
                            size="small"
                            aria-label={`Delete ${c.name}`}
                            onClick={() => {
                              setDeleteErr(null);
                              setDeleting(c);
                            }}
                            data-testid={`company-delete-${c.id}`}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {unassigned > 0 && (
                <TableRow
                  hover
                  onClick={() => navigate(`/premises?company=unassigned`)}
                  sx={{ cursor: "pointer" }}
                  data-testid="company-row-unassigned"
                >
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontStyle: "italic", color: "text.secondary" }}
                    >
                      (Unassigned)
                    </Typography>
                  </TableCell>
                  <TableCell />
                  <TableCell align="right">
                    {unassigned} {unassigned === 1 ? "premise" : "premises"}
                  </TableCell>
                  <TableCell />
                </TableRow>
              )}
              {companies?.length === 0 && unassigned === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Box
                      sx={{
                        py: 5,
                        textAlign: "center",
                        color: "text.secondary",
                      }}
                    >
                      <Typography variant="body2">No companies yet.</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <CreateCompanyDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(c) => {
          setCreateOpen(false);
          setToast({ msg: `Created ${c.name}`, severity: "success" });
          refresh();
        }}
        onError={(msg) => setToast({ msg, severity: "error" })}
      />

      <CompanyEnergyDialog
        open={!!energyFor}
        companyId={energyFor?.id ?? null}
        companyName={energyFor?.name ?? ""}
        onClose={() => setEnergyFor(null)}
      />

      <DangerConfirmDialog
        open={!!deleting}
        title="Delete company"
        entityName={deleting?.name ?? ""}
        body={
          <Typography variant="body2" color="text.secondary">
            Companies with active premises can&apos;t be removed - reassign or
            delete those premises first. This action only deletes the tenant
            record itself; user assignments referencing it stay intact and will
            need cleanup.
          </Typography>
        }
        errorMessage={deleteErr}
        busy={deleteBusy}
        onCancel={() => {
          if (deleteBusy) return;
          setDeleting(null);
          setDeleteErr(null);
        }}
        onConfirm={async () => {
          if (!deleting) return;
          setDeleteBusy(true);
          setDeleteErr(null);
          try {
            await foyerApi.deleteCompany(deleting.id);
            setToast({
              msg: `Deleted ${deleting.name}`,
              severity: "success",
            });
            setDeleting(null);
            refresh();
          } catch (e) {
            setDeleteErr(e instanceof Error ? e.message : String(e));
          } finally {
            setDeleteBusy(false);
          }
        }}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {toast ? (
          <Alert severity={toast.severity}>{toast.msg}</Alert>
        ) : undefined}
      </Snackbar>
    </PageContainer>
  );
}

function CreateCompanyDialog({
  open,
  onClose,
  onCreated,
  onError,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (c: CompanySummary) => void;
  onError: (msg: string) => void;
}) {
  const [name, setName] = useState("");
  const [type, setType] = useState<CompanyType>("CUSTOMER");
  const [busy, setBusy] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName("");
      setType("CUSTOMER");
      setNameError(null);
    }
  }, [open]);

  async function submit() {
    setBusy(true);
    setNameError(null);
    try {
      const created = await foyerApi.createCompany({ name, type });
      onCreated(created);
    } catch (e) {
      if (errorCode(e) === "company_already_exists") {
        setNameError(friendlyError(e));
      } else {
        onError(friendlyError(e));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>New company</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setNameError(null);
            }}
            autoFocus
            required
            size="small"
            error={!!nameError}
            helperText={nameError ?? undefined}
            inputProps={{ "data-testid": "company-name" }}
          />
          <TextField
            select
            label="Type"
            value={type}
            onChange={(e) => setType(e.target.value as CompanyType)}
            size="small"
            inputProps={{ "data-testid": "company-type" }}
          >
            {COMPANY_TYPES.map((t) => (
              <MenuItem key={t.key} value={t.key}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={busy || !name.trim()}
          onClick={submit}
          data-testid="company-create"
        >
          {busy ? "Creating…" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
